'use client'

import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useDeleteShippingRates } from '@/lib/api/shippingRates'
import { useToast } from '@/components/ui/use-toast'

interface BulkDeleteShippingRatesModalProps {
	shippingRateIds: string[]
	onClose: () => void
	onSuccess?: () => void
}

export const BulkDeleteShippingRatesModal = ({
	shippingRateIds,
	onClose,
	onSuccess,
}: BulkDeleteShippingRatesModalProps) => {
	const { t } = useTranslation('shippings')
	const { t: tCommon } = useTranslation('common')
	const { toast } = useToast()
	const deleteShippingRates = useDeleteShippingRates()

	const handleDelete = async () => {
		if (!shippingRateIds.length) return

		try {
			await deleteShippingRates.mutateAsync({ ids: shippingRateIds })

			toast({
				title: tCommon('success'),
				description: t('deleteBulkSuccess', { count: shippingRateIds.length }),
			})

			onSuccess?.()
			onClose()
		} catch (error) {
			toast({
				title: tCommon('error'),
				description: t('deleteBulkError'),
				variant: 'destructive',
			})
		}
	}

	return (
		<AlertDialog open={true} onOpenChange={onClose}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{t('modals.bulkDelete.title')}</AlertDialogTitle>
					<AlertDialogDescription>
						{t('modals.bulkDelete.description', { count: shippingRateIds.length })}
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<Button variant="outline" onClick={onClose} disabled={deleteShippingRates.isPending}>
						{tCommon('cancel')}
					</Button>
					<Button
						variant="destructive"
						onClick={handleDelete}
						disabled={deleteShippingRates.isPending}
					>
						{deleteShippingRates.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						{tCommon('delete')}
					</Button>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}

